import React from "react";

/**
 * A ref callback that scrolls the focused row into view.
 *
 * Opening the right record is not enough on a long table — the operator still
 * has to find it. Written as a ref callback rather than a hook because rows are
 * produced inside `.map`, where a hook cannot be called.
 *
 * `block: "center"` rather than the default: a row scrolled to the very top
 * sits under the sticky header on these pages.
 */
export const scrollRowIntoView =
  (active: boolean) => (element: HTMLElement | null) => {
    if (!active || !element) return;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  };

/** Ring applied to the row an operator was sent here to see. */
export const focusRowClass = (active: boolean): string =>
  active ? "ring-2 ring-inset ring-indigo-400 bg-indigo-50/60" : "";

/**
 * Shown when the record is not in the loaded page.
 *
 * These lists are paginated and some are filtered, so a focused record can
 * legitimately be absent. Saying so beats leaving the operator to wonder why
 * nothing happened — they were sent here by an alert and expect to land on
 * something.
 */
export const FocusNotFound: React.FC<{ onDismiss: () => void }> = ({
  onDismiss,
}) => (
  <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
    <p className="text-sm text-amber-800">
      The item you followed here is not on this page — it may be older than the
      current filter or further down the list. Try clearing the filters or
      loading more.
    </p>
    <button
      onClick={onDismiss}
      className="text-sm font-medium text-amber-800 hover:underline"
    >
      Dismiss
    </button>
  </div>
);

export default FocusNotFound;
